const HTML_TAG_REGEX = /<\/?[a-z][\s\S]*>/i;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const INVALID_EMAIL_CHARS = /[\s,;]/;
const PHONE_REGEX = /^\d{10}$/;

/** @type {(() => void) | null} */
let recaptchaOnload = null;

/**
 * @typedef {object} ContactUsConfig
 * @property {string} embedToken
 * @property {string} departmentId
 * @property {string} recaptchaSiteKey
 * @property {string} chatFromUrl
 */

/**
 * @typedef {object} ContactUsI18n
 * @property {Record<string, string>} [errors]
 */

/**
 * @extends {HTMLElement}
 */
class ContactUsForm extends HTMLElement {
  /** @type {ContactUsConfig | null} */
  #config = null;

  /** @type {ContactUsI18n} */
  #i18n = {};

  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {string} */
  #recaptchaToken = '';

  /** @type {number | null} */
  #recaptchaWidgetId = null;

  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';

    this.#config = this.#parseConfig();
    this.#i18n = this.#parseI18n();
    this.#form = this.querySelector('form');

    if (!this.#form || !this.#config) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#form.addEventListener('submit', this.#handleSubmit, { signal });

    for (const field of this.#form.elements) {
      if (!(field instanceof HTMLElement)) continue;
      if (!field.getAttribute('name')) continue;

      field.addEventListener('input', () => this.#clearFieldError(field.getAttribute('name')), { signal });
      field.addEventListener('change', () => this.#clearFieldError(field.getAttribute('name')), { signal });
    }

    if (this.#config.recaptchaSiteKey) {
      this.#loadRecaptcha();
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * @returns {ContactUsConfig | null}
   */
  #parseConfig() {
    const el = this.querySelector('[data-config]');
    if (!el?.textContent) return null;

    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  /**
   * @returns {ContactUsI18n}
   */
  #parseI18n() {
    const el = this.querySelector('[data-i18n]');
    if (!el?.textContent) return {};

    try {
      return JSON.parse(el.textContent);
    } catch {
      return {};
    }
  }

  /**
   * @param {string | null} key
   * @returns {string}
   */
  #errorMessage(key) {
    const errors = this.#i18n.errors || {};
    if (!key) return errors.generic || '';
    return errors[key] || errors.generic || '';
  }

  #loadRecaptcha() {
    const container = this.querySelector('[data-recaptcha]');
    if (!(container instanceof HTMLElement) || !this.#config?.recaptchaSiteKey) return;

    if (window.grecaptcha?.render) {
      this.#renderRecaptcha(container);
      return;
    }

    recaptchaOnload = () => {
      const target = this.querySelector('[data-recaptcha]');
      if (target instanceof HTMLElement) {
        this.#renderRecaptcha(target);
      }
    };

    window.nuloContactUsRecaptchaOnload = () => {
      recaptchaOnload?.();
    };

    if (document.querySelector('script[data-nulo-recaptcha]')) return;

    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?onload=nuloContactUsRecaptchaOnload&render=explicit';
    script.async = true;
    script.defer = true;
    script.dataset.nuloRecaptcha = 'true';
    document.head.appendChild(script);
  }

  /**
   * @param {HTMLElement} container
   */
  #renderRecaptcha(container) {
    if (!window.grecaptcha?.render || !this.#config?.recaptchaSiteKey) return;
    if (this.#recaptchaWidgetId != null) return;

    this.#recaptchaWidgetId = window.grecaptcha.render(container, {
      sitekey: this.#config.recaptchaSiteKey,
      callback: (token) => {
        this.#recaptchaToken = token;
        this.#clearCaptchaError();
      },
      'expired-callback': () => {
        this.#recaptchaToken = '';
      },
    });
  }

  /**
   * @param {Event} event
   */
  #handleSubmit = async (event) => {
    event.preventDefault();
    if (!this.#form || !this.#config) return;

    const formData = new FormData(this.#form);
    const values = {
      firstName: String(formData.get('firstName') || '').trim(),
      lastName: String(formData.get('lastName') || '').trim(),
      email: String(formData.get('email') || '').trim(),
      phoneNumber: String(formData.get('phoneNumber') || '').trim(),
      country: String(formData.get('country') || '').trim(),
      do_you_have_a_cat_or_dog_: String(formData.get('do_you_have_a_cat_or_dog_') || '').trim(),
      note: String(formData.get('note') || '').trim(),
    };

    const errors = this.#validate(values);
    const requiresCaptcha = Boolean(this.#config.recaptchaSiteKey);

    if (requiresCaptcha && !this.#recaptchaToken) {
      this.#showCaptchaError();
    } else {
      this.#clearCaptchaError();
    }

    this.#renderErrors(errors);

    if (Object.keys(errors).length > 0 || (requiresCaptcha && !this.#recaptchaToken)) {
      return;
    }

    this.#setSubmitting(true);

    try {
      const body = new URLSearchParams({
        name: `${values.firstName} ${values.lastName}`,
        email: values.email,
        phone: values.phoneNumber,
        message: values.note,
        chatFromUrl: this.#config.chatFromUrl,
        departmentId: this.#config.departmentId,
        customPrechatFields: JSON.stringify({
          'PCF-9d46': [values.do_you_have_a_cat_or_dog_],
          'PCF-c568': [values.country],
          'PCF-54db': [values.phoneNumber],
        }),
      });

      await fetch(`https://happyfoxchat.com/company/${this.#config.embedToken}/offline`, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      this.#showSuccess();
    } catch {
      this.#showGenericError();
    } finally {
      this.#setSubmitting(false);
    }
  };

  /**
   * @param {Record<string, string>} values
   * @returns {Record<string, string>}
   */
  #validate(values) {
    /** @type {Record<string, string>} */
    const errors = {};
    const err = this.#i18n.errors || {};

    if (!values.firstName) {
      errors.firstName = err.firstName || '';
    } else if (!this.#isSafeText(values.firstName)) {
      errors.firstName = err.firstNameHtml || '';
    }

    if (!values.lastName) {
      errors.lastName = err.lastName || '';
    } else if (!this.#isSafeText(values.lastName)) {
      errors.lastName = err.lastNameHtml || '';
    }

    if (!values.email) {
      errors.email = err.email || '';
    } else if (!this.#isValidEmail(values.email)) {
      errors.email = err.emailInvalid || '';
    }

    if (!values.phoneNumber) {
      errors.phoneNumber = err.phoneNumber || '';
    } else if (!PHONE_REGEX.test(values.phoneNumber)) {
      errors.phoneNumber = err.phoneInvalid || '';
    }

    if (!values.country) {
      errors.country = err.country || '';
    } else if (!this.#isSafeText(values.country)) {
      errors.country = err.countryHtml || '';
    }

    if (!values.do_you_have_a_cat_or_dog_) {
      errors.do_you_have_a_cat_or_dog_ = err.do_you_have_a_cat_or_dog_ || '';
    } else if (!this.#isSafeText(values.do_you_have_a_cat_or_dog_)) {
      errors.do_you_have_a_cat_or_dog_ = err.petHtml || '';
    }

    if (!values.note) {
      errors.note = err.note || '';
    } else if (!this.#isSafeText(values.note)) {
      errors.note = err.noteHtml || '';
    }

    return errors;
  }

  /**
   * @param {string} value
   * @returns {boolean}
   */
  #isSafeText(value) {
    return !HTML_TAG_REGEX.test(value);
  }

  /**
   * @param {string} email
   * @returns {boolean}
   */
  #isValidEmail(email) {
    return EMAIL_REGEX.test(email) && !INVALID_EMAIL_CHARS.test(email) && !HTML_TAG_REGEX.test(email);
  }

  /**
   * @param {Record<string, string>} errors
   */
  #renderErrors(errors) {
    for (const el of this.querySelectorAll('[data-error-for]')) {
      if (!(el instanceof HTMLElement)) continue;

      const field = el.getAttribute('data-error-for');
      const message = field ? errors[field] : '';

      if (message) {
        el.textContent = message;
        el.hidden = false;
      } else {
        el.textContent = '';
        el.hidden = true;
      }
    }
  }

  /**
   * @param {string | null} fieldName
   */
  #clearFieldError(fieldName) {
    if (!fieldName) return;

    const el = this.querySelector(`[data-error-for="${fieldName}"]`);
    if (el instanceof HTMLElement) {
      el.textContent = '';
      el.hidden = true;
    }
  }

  #showCaptchaError() {
    const el = this.querySelector('[data-captcha-error]');
    if (!(el instanceof HTMLElement)) return;

    el.textContent = this.#errorMessage('captcha');
    el.hidden = false;
  }

  #clearCaptchaError() {
    const el = this.querySelector('[data-captcha-error]');
    if (!(el instanceof HTMLElement)) return;

    el.textContent = '';
    el.hidden = true;
  }

  #showGenericError() {
    const noteError = this.querySelector('[data-error-for="note"]');
    if (!(noteError instanceof HTMLElement)) return;

    noteError.textContent = this.#errorMessage('generic');
    noteError.hidden = false;
  }

  #showSuccess() {
    const form = this.querySelector('.contact-us__form');
    const success = this.querySelector('[data-success]');

    if (form instanceof HTMLElement) form.hidden = true;
    if (success instanceof HTMLElement) {
      success.hidden = false;
      success.focus();
    }
  }

  /**
   * @param {boolean} isSubmitting
   */
  #setSubmitting(isSubmitting) {
    const button = this.querySelector('.contact-us__submit');
    const spinner = this.querySelector('[data-submit-spinner]');

    if (button instanceof HTMLButtonElement) {
      button.disabled = isSubmitting;
      button.setAttribute('aria-busy', String(isSubmitting));
    }

    if (spinner instanceof HTMLElement) {
      spinner.hidden = !isSubmitting;
    }
  }
}

if (!customElements.get('contact-us-form')) {
  customElements.define('contact-us-form', ContactUsForm);
}
